import { Chip, ChipProps } from "@mui/material";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { format } from "date-fns";
import { useCallback, useMemo, useState } from "react";
import { PatientSubscriptions, SubscriptionStatus } from "src/api/types";
import { useSubscriptionModal } from "src/context/SubscriptionModalProvider/useSubscriptionModal";
import { isError } from "src/utils/isError";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import {
  useGetScheduledReactivationQuery,
  useReactivateSubscriptionMutation,
} from "src/api/services/subscriptionsApi";
import AlertWithCopy from "src/components/AlertWithCopy";
import { skipToken } from "@reduxjs/toolkit/query";
import SubscriptionText from "../SubscriptionText";
import ScheduledReactivationAlert from "../ScheduledReactivationAlert";

interface Props {
  subscription: PatientSubscriptions;
  allowActions?: boolean;
}

const mapStatusToChipLabel: Record<
  SubscriptionStatus,
  { label: string; color: ChipProps["color"] }
> = {
  scheduled_for_cancellation: {
    label: "Scheduled For Cancellation",
    color: "warning",
  },
  canceled: { label: "Canceled", color: "error" },
  scheduled_for_reactivation: {
    label: "Scheduled For Reactivation",
    color: "warning",
  },
  reactivated: {
    label: "Reactivated",
    color: "primary",
  },
  paid: {
    label: "Active",
    color: "success",
  },
  unpaid: {
    label: "Unpaid",
    color: "warning",
  },
  draft: {
    label: "Draft",
    color: "default",
  },
};

const Subscription = ({ subscription, allowActions = false }: Props) => {
  const {
    handleOpenChangePlan,
    handleOpenCancelPlan,
    handleOpenAddCoupon,
    handleOpenScheduleReactivation,
  } = useSubscriptionModal();
  const { data: scheduledReactivation } = useGetScheduledReactivationQuery(
    subscription.status === "canceled" ? subscription.id : skipToken,
  );

  const [reactivateSubscription] = useReactivateSubscriptionMutation();
  const errorLog = useSentryLogger("error");
  const [alert, setAlert] = useState<boolean>(false);
  const [refillUrl, setRefillUrl] = useState<string>("");

  const handleChangePlan = useCallback(
    () => handleOpenChangePlan(subscription),
    [handleOpenChangePlan, subscription],
  );
  const handleCancel = useCallback(
    () => handleOpenCancelPlan(subscription),
    [handleOpenCancelPlan, subscription],
  );

  const handleAddCoupon = useCallback(
    () => handleOpenAddCoupon(subscription),
    [handleOpenAddCoupon, subscription],
  );

  const canceled = useMemo(() => {
    const date = subscription.canceled_at
      ? new Date(subscription.canceled_at)
      : null;

    return subscription.status === "canceled" && date && !isNaN(date.getTime())
      ? format(date, "MMMM dd, yyyy")
      : null;
  }, [subscription.canceled_at, subscription.status]);

  const handleReactivate = useCallback(async () => {
    try {
      const reactivation = await reactivateSubscription({
        id: subscription.id,
        patient_id: subscription.patient_id,
      }).unwrap();

      if ("refill_url" in reactivation) {
        const appUrl =
          import.meta.env.VITE_REDIRECT_LINK ?? "https://app.holavida.com";
        const refillUrl = `${appUrl}${reactivation.refill_url}`;

        setRefillUrl(refillUrl);
        setAlert(true);
      }
    } catch (err: unknown) {
      if (isError(err)) {
        errorLog(err.data.message);
      } else {
        errorLog(`Unable to reactivate subscription`);
      }
    }
  }, [
    errorLog,
    reactivateSubscription,
    subscription.id,
    subscription.patient_id,
  ]);

  const handleScheduleReactivation = useCallback(
    () => handleOpenScheduleReactivation(subscription),
    [handleOpenScheduleReactivation, subscription],
  );

  return (
    <Stack
      gap={0.5}
      p={1}
      sx={{ border: "1px solid #000000", borderRadius: "5px" }}
    >
      <Stack direction={"row"} gap={2}>
        <Typography fontWeight={700}>{subscription.display_name}</Typography>
        <Chip
          color={mapStatusToChipLabel[subscription.status]?.color}
          label={mapStatusToChipLabel[subscription.status]?.label}
          sx={{ fontSize: "1rem" }}
        />
      </Stack>
      <Typography>{`Membership: $${subscription.price.amount} / ${subscription.price.interval_count} days`}</Typography>

      <SubscriptionText subscription={subscription} />

      {allowActions ? (
        canceled && scheduledReactivation == null ? (
          alert ? (
            <AlertWithCopy
              copyText={refillUrl}
              text={`Subscription reactivated. Please copy and send the refill URL to the patient to complete the process: ${refillUrl}`}
              contentType="refill link"
            />
          ) : (
            <Stack direction="row" gap={1}>
              <Button sx={{ width: "fit-content" }} onClick={handleReactivate}>
                Reactivate
              </Button>

              <Button
                variant="secondary"
                sx={{ width: "fit-content" }}
                onClick={handleScheduleReactivation}
              >
                Schedule Reactivate
              </Button>
            </Stack>
          )
        ) : scheduledReactivation != null ? (
          <ScheduledReactivationAlert
            scheduledReactivation={scheduledReactivation}
          />
        ) : (
          <Stack direction="row" gap={1}>
            <Button sx={{ width: "fit-content" }} onClick={handleChangePlan}>
              Change
            </Button>
            <Button
              variant="secondary"
              sx={{ width: "fit-content" }}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              variant="outlined"
              sx={{ width: "fit-content" }}
              onClick={handleAddCoupon}
            >
              Add coupon
            </Button>
          </Stack>
        )
      ) : null}
    </Stack>
  );
};

export default Subscription;
